import { Header as KobleHeader, Locale, SkipLink } from '@innovationnorway/cl-react';
import { onlineManager } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { headerLocalization } from '../assets/localizations/localizationsHeader';
import { AppContext } from '../context/app-context';
import { ConfigurationActionTypes } from '../context/configuration-reducer';
import { useLocalizer } from '../hooks';
import { ApiSchema, apiClient } from '../lib/query/apiClient';
import { queryClient } from '../lib/query/tanStackQueryProvider';
import { Language } from '../models';
import { setLanguage } from '../services/session.service';

/**
 * To satisfy typescript we supply this constant. It also acts as a visual marker in the UI for when data is missing.
 */
const MISSING = 'MISSING';
type SessionConfiguration = ApiSchema<'SessionConfiguration'>;

export const Header = () => {
  const configurationQueryKey = ['get', '/api/v1/Session/current/configuration'] as const;

  const { dispatch } = useContext(AppContext);
  const { typeSafeL } = useLocalizer('NewHeader');
  const { globalL } = useLocalizer('global');

  const localization = typeSafeL<typeof headerLocalization>;
  const navigate = useNavigate();

  const { data: configurationData } = apiClient.useQuery('get', '/api/v1/Session/current/configuration');

  async function handleLanguageChange(lang: Language) {
    await setLanguage(lang);

    dispatch({
      type: ConfigurationActionTypes.SetLanguage,
      payload: lang,
    });

    // Invalidate service messages query to refresh messages after language change
    queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/ServiceMessages/GetServiceMessages'] });

    // Invalidate configuration query to refresh language in header
    queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/Session/current/configuration'] });

    // Invalidate footer query data to refresh footer content after language change
    queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/Session/GetFooterData'] });
  }

  const { data: headerData } = apiClient.useQuery('get', '/api/v1/Session/GetHeaderData');
  const { mutateAsync: changeAccountMutation } = apiClient.useMutation('post', '/api/v1/Session/ChangeAccount', {
    networkMode: 'always',
    onMutate: async (variables) => {
      // Force loading while account switch is in-flight
      onlineManager.setOnline(false);

      await queryClient.cancelQueries();

      const previousConfiguration = queryClient.getQueryData<SessionConfiguration>(configurationQueryKey);
      const optimisticAccountPublicId = variables?.params?.query?.accountId;

      if (optimisticAccountPublicId) {
        queryClient.setQueryData<SessionConfiguration>(configurationQueryKey, (previous) => {
          if (!previous?.user) return previous;

          return {
            ...previous,
            user: {
              ...previous.user,
              accountPublicId: optimisticAccountPublicId,
            },
          };
        });
      }

      queryClient.resetQueries({
        predicate: (query) => {
          const [method, path] = query.queryKey;

          // Keep these to avoid flicker in header/service-messages
          if (method === 'get' && (path as string).startsWith('/api/v1/ServiceMessages/GetServiceMessages')) return false;
          if (method === 'get' && (path as string).startsWith('/api/v1/Session/GetHeaderData')) return false;
          if (method === 'get' && (path as string).startsWith('/api/v1/Session/current/configuration')) return false;

          return true;
        },
      });

      return { previousConfiguration };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousConfiguration) {
        queryClient.setQueryData(configurationQueryKey, context.previousConfiguration);
      }
    },
    onSettled: async () => {
      // Resume network and synchronize cache with server truth
      onlineManager.setOnline(true);
      await queryClient.invalidateQueries();
    },
  });

  async function changeAccount(accountName: string | undefined, accountId: string, profileId: string) {
    navigate('/bankaccounts?actorChangedTo=' + encodeURIComponent(accountName ?? ''));

    await changeAccountMutation({
      params: { query: { accountId, profileId } },
    });
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const actorChangedTo = searchParams.get('actorChangedTo');
  const [actorChangedToToast, setActorChangedToToast] = useState<string | null>(null);

  useEffect(() => {
    if (!actorChangedTo) return;

    setActorChangedToToast(actorChangedTo);

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('actorChangedTo');
    setSearchParams(nextSearchParams, { replace: true });
  }, [actorChangedTo, searchParams, setSearchParams]);

  const homePage = headerData?.links?.homePage;

  return (
    <>
      <SkipLink href="#main-content">{globalL('skipToMainContent')}</SkipLink>
      <KobleHeader
        logo={{ name: 'in', href: homePage?.url ?? MISSING, label: localization('bankLabel') }}
        languageMenu={{
          languages: [
            { locale: Language.nb, isSelected: configurationData?.language === Language.nb, title: 'Bokmål', onClick: () => handleLanguageChange(Language.nb) },
            { locale: Language.nn, isSelected: configurationData?.language === Language.nn, title: 'Nynorsk', onClick: () => handleLanguageChange(Language.nn) },
            { locale: Language.en, isSelected: configurationData?.language === Language.en, title: 'English', onClick: () => handleLanguageChange(Language.en) },
          ],
        }}
        locale={configurationData?.language as Locale}
        accountMenu={{
          accounts:
            headerData?.userAccounts?.map((acc) => {
              return {
                accountName: acc.accountName!,
                isSelected: acc.publicId === configurationData?.user?.accountPublicId,
                onClick: () => changeAccount(acc.accountName, acc.publicId!, acc.userAccountId!),
                limitedAccess: !acc.hasRole,
              };
            }) ?? [],
          displayCompanyDataError: headerData?.userAccounts?.length === 0,
        }}
        user={{
          name: configurationData?.user?.displayName ?? MISSING,
          links: {
            homePage: {
              title: homePage?.title ?? MISSING,
              url: homePage?.url ?? MISSING,
            },
            pages: (headerData?.links?.pages ?? []).flatMap((navItem) => (navItem.title && navItem.url ? [{ title: navItem.title, url: navItem.url }] : [])),
          },

          actions: [
            {
              url: '/sign-out',
              title: localization('logOutLabel'),
            },
          ],

          footerLink: {
            title: 'innovasjonnorge.no',
            url: 'https://www.innovasjonnorge.no',
          },
        }}
        showAccountConfirmation={actorChangedToToast}
        notificationMenu={{
          displayNotificationsDataError: headerData?.notificationsStatus !== 'Ok',
          items:
            headerData?.userAccounts
              ?.filter((ua) => (ua.numberOfNotifications ?? 0) > 0 && ua.hasRole)
              .map((ua) => ({
                accountName: ua.accountName!,
                isSelected: ua.publicId === configurationData?.user?.accountPublicId,
                notifications: ua.notifications!.map((notification) => ({
                  title: notification.displayTaskType![configurationData?.language ?? Language.nb],
                  url: ua.publicId === configurationData?.user?.accountPublicId ? notification.url! : undefined,
                  onClick: () => {
                    // We do not have to switch account if we are already on the correct account
                    if (ua.publicId === configurationData?.user?.accountPublicId) return;

                    changeAccount(ua.accountName, ua.publicId!, ua.userAccountId!);

                    navigate(notification.url! + '?actorChangedTo=' + encodeURIComponent(ua.accountName ?? ''));
                  },
                })),
                count: ua.numberOfNotifications,
                viewAllButton: {
                  url: ua.publicId === configurationData?.user?.accountPublicId ? `${homePage?.url}/notifications` : undefined,
                  onClick: () => {
                    // We do not have to switch account if we are already on the correct account
                    if (ua.publicId === configurationData?.user?.accountPublicId) return;

                    changeAccount(ua.accountName, ua.publicId!, ua.userAccountId!);

                    navigate(`${homePage?.url}/notifications?actorChangedTo=` + encodeURIComponent(ua.accountName ?? ''));
                  },
                },
              })) ?? [],
        }}
      />
    </>
  );
};
